import { all, call, put, takeLatest } from 'redux-saga/effects';
import { requestSagaFactory } from 'helpers';
import { IAction, IPagination } from 'models';
import { ENDPOINTS, logSagaErrorMessage } from 'utils';
import {
	IPersonaRole,
	IPersonaSatisfaction,
	IPersonaPayload,
	IPersonaData,
	IPersonaTrendsData,
	IPersonaTrendData,
	IPersonaRawTrendData,
	IResultComments,
} from 'modules/admin-panel/pages/results/models';
import {
	getPersonaList,
	getPersonaComments,
	getPersonaSatisfaction,
	getPersonaTrends,
	getPersonaTypicalRoles,
	getPersonaDetails,
	getPersonaCommentsWordcloud,
} from 'modules/admin-panel/pages/results/results.actions';
import * as types from 'modules/admin-panel/pages/results/ActionTypes';

const getPersonaTrendsSaga = requestSagaFactory({
	url: ENDPOINTS.personaTrends,
	actionApi: getPersonaTrends,
	logErrorMessage: 'Get persona trends',
});

const getPersonaTypicalRolesSaga = requestSagaFactory({
	url: ENDPOINTS.personaRoles,
	actionApi: getPersonaTypicalRoles,
	logErrorMessage: 'Get persona typical roles',
});

const getPersonaSatisfactionSaga = requestSagaFactory({
	url: ENDPOINTS.personaSatisfaction,
	actionApi: getPersonaSatisfaction,
	logErrorMessage: 'Get persona overall satisfaction',
});

const getPersonaCommentsSaga = requestSagaFactory({
	url: ENDPOINTS.personaComments,
	actionApi: getPersonaComments,
	logErrorMessage: 'Get persona comments',
});

const getPersonaCommentsWordcloudSaga = requestSagaFactory({
	url: ENDPOINTS.personaCommentsWordcloud,
	actionApi: getPersonaCommentsWordcloud,
	logErrorMessage: 'Get persona comments wordcloud',
});

export const getPersonaListSaga = requestSagaFactory<
	IPersonaPayload,
	Array<IPersonaData>
>({
	actionApi: getPersonaList,
	url: ENDPOINTS.personaData,
	logErrorMessage: 'Get persona list',
});

// const getPersonaWorkStyleSaga = requestSagaFactory({
// 	url: ENDPOINTS.personaWorkStyle,
// 	actionApi: getPersonaWorkStyle,
// 	logErrorMessage: 'Get persona work style',
// });
//
// const getPersonaRespondentsSaga = requestSagaFactory({
// 	url: ENDPOINTS.personaRespondents,
// 	actionApi: getPersonaRespondents,
// 	logErrorMessage: 'Get persona respondents',
// });

function* getPersonaDetailsSaga({
	payload: { personaId, surveyRoundId },
}: IAction<{ personaId: string; surveyRoundId: string }>) {
	try {
		yield put(getPersonaDetails.loading());

		const personaIdPayload = { personaId };
		const surveyRoundIdPayload = { surveyRoundId };

		const [personaList, trends, roles, satisfaction]: [
			IPersonaData[],
			IPersonaTrendsData<IPersonaRawTrendData>,
			IPagination<IPersonaRole[]>,
			IPersonaSatisfaction,
			IPagination<IResultComments[]>,
		] = yield all([
			call(getPersonaListSaga, { payload: surveyRoundIdPayload }),
			call(getPersonaTrendsSaga, { payload: personaIdPayload }),
			call(getPersonaTypicalRolesSaga, { payload: personaIdPayload }),
			call(getPersonaSatisfactionSaga, { payload: personaIdPayload }),
		]);

		const personaItem = personaList?.find(
			(currPersonaItem) =>
				currPersonaItem.personaId === parseInt(personaId, 10),
		);

		const trendsData: IPersonaTrendData[] = trends?.serviceTrends.map((i) => ({
			serviceName: i.serviceName,
			parentId: i.parentId,
			...i.round,
		}));

		yield put(
			getPersonaDetails.success({
				personaItem,
				trends: { ...trends, serviceTrends: trendsData },
				roles: roles.content,
				satisfaction,
			}),
		);
	} catch (e) {
		logSagaErrorMessage('Get persona details', e);
		yield put(getPersonaDetails.fail(e));
	}
}

export function* personaSaga() {
	yield all([
		takeLatest(types.GET_PERSONA_DETAILS.DEFAULT, getPersonaDetailsSaga),
		takeLatest(types.GET_PERSONA_COMMENTS.DEFAULT, getPersonaCommentsSaga),
		takeLatest(
			types.GET_PERSONA_COMMENTS_WORDCLOUD.DEFAULT,
			getPersonaCommentsWordcloudSaga,
		),
		takeLatest(types.GET_PERSONA_TRENDS.DEFAULT, getPersonaTrendsSaga),
	]);
}
